// Silo aéroport — index /transfert-aeroport|airport-transfer|flughafentransfer
import { Link } from "react-router-dom";
import { ArrowRight, Plane } from "lucide-react";
import { JsonLd, SeoPage, TrustChips, useHreflang, useSeo } from "@/components/seo/Seo";
import { AIRPORTS, BASE_URL, LANGS, S, T, p, routesFrom } from "@/lib/seoContent";

export const AIRPORT_INDEX_COPY = {
  fr: {
    title: "Transfert aéroport Suisse | Genève & Zurich | MILES",
    meta: "Transferts privés depuis les aéroports de Genève et Zurich : accueil au terminal, suivi de vol automatique, attente offerte, prix fixe. Réservation en ligne.",
    h1: "L'aéroport, sans la file de taxis.",
    intro: "À Genève comme à Zurich, votre chauffeur MILES suit votre vol, vous attend au terminal et conduit vos bagages. Le prix est fixé avant l'atterrissage.",
  },
  en: {
    title: "Airport Transfer Switzerland | Geneva & Zurich | MILES",
    meta: "Private transfers from Geneva and Zurich airports: terminal meet & greet, automatic flight tracking, free waiting time, fixed price. Book online.",
    h1: "The airport, without the taxi queue.",
    intro: "In Geneva as in Zurich, your MILES chauffeur tracks your flight, waits at the terminal and carries your bags. The price is fixed before you land.",
  },
  de: {
    title: "Flughafentransfer Schweiz | Genf & Zürich | MILES",
    meta: "Private Transfers ab Flughafen Genf und Zürich: Empfang am Terminal, automatische Flugverfolgung, kostenlose Wartezeit, Fixpreis. Online buchen.",
    h1: "Der Flughafen, ohne Taxi-Warteschlange.",
    intro: "In Genf wie in Zürich verfolgt Ihr MILES-Fahrer Ihren Flug, wartet am Terminal und trägt Ihr Gepäck. Der Preis steht vor der Landung fest.",
  },
};

export default function AirportIndex({ lang }) {
  const c = AIRPORT_INDEX_COPY[lang];
  const t = T[lang];
  const path = p(lang, S.airport[lang]);
  const alternates = Object.fromEntries(LANGS.map((l) => [l, p(l, S.airport[l])]));
  useSeo({ lang, title: c.title, description: c.meta, path, type: "airport-index", id: "" });
  useHreflang(alternates);

  const crumbs = [{ label: t.home, to: p(lang) }, { label: { fr: "Transferts aéroport", en: "Airport transfers", de: "Flughafentransfers" }[lang] }];
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((x, i) => ({ "@type": "ListItem", position: i + 1, name: x.label, ...(x.to ? { item: `${BASE_URL}${x.to}` } : {}) })) },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId="seo-airport-index">
      {jsonLd.map((j, i) => <JsonLd key={i} id={`apt-${i}`} data={j} />)}
      <h1 className="max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-5xl" data-testid="airport-index-h1">{c.h1}</h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate">{c.intro}</p>
      <div className="mt-8"><TrustChips lang={lang} ski /></div>

      <div className="mt-12 grid gap-4 md:grid-cols-2">
        {Object.values(AIRPORTS).map((a) => {
          const routes = routesFrom(a.id);
          return (
            <Link key={a.id} to={p(lang, S.airport[lang], a.slug[lang])} data-testid={`airport-card-${a.id}`}
              className="group rounded-3xl border border-ink/10 bg-white p-7 transition-colors hover:border-miles/40">
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-miles-deep font-mono text-xs font-bold text-white">{a.iata}</span>
                <Plane className="h-5 w-5 text-slate transition-colors group-hover:text-miles" strokeWidth={1.5} />
              </div>
              <p className="mt-4 text-lg font-bold text-ink">{a.name[lang]}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate">{a.intro[lang]}</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-miles">{routes.length} {t.routes_title.toLowerCase()}</p>
            </Link>
          );
        })}
      </div>
    </SeoPage>
  );
}
